import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

// Photo-backed industry cards (Unsplash direct URLs, tuned for professional context).
const INDUSTRIES = [
  { name: "Financial Services", img: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=900&q=80" },
  { name: "SMEs", img: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=900&q=80" },
  { name: "Startups", img: "https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=900&q=80" },
  { name: "Agriculture", img: "https://images.unsplash.com/photo-1500595046743-cd271d694d30?auto=format&fit=crop&w=900&q=80" },
  { name: "Restaurants", img: "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?auto=format&fit=crop&w=900&q=80" },
  { name: "Hotels", img: "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=900&q=80" },
  { name: "Retail", img: "/ops-retail-ghana-v3.png" },
  { name: "Healthcare", img: "https://images.unsplash.com/photo-1584982751601-97dcc096659c?auto=format&fit=crop&w=900&q=80" },
  { name: "Education", img: "https://images.unsplash.com/photo-1497633762265-9d179a990aa6?auto=format&fit=crop&w=900&q=80" },
  { name: "Government", img: "https://images.unsplash.com/photo-1584438784894-089d6a62b8fa?auto=format&fit=crop&w=900&q=80" },
  { name: "NGOs", img: "https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?auto=format&fit=crop&w=900&q=80" },
  { name: "Manufacturing", img: "https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?auto=format&fit=crop&w=900&q=80" },
  { name: "Professional Services", img: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=900&q=80" },
];

export default function OpsIndustriesCards() {
  return (
    <section id="industries" data-testid="section-industries-cards" className="relative bg-[#F1ECE0] text-[#1A2E4A] border-t border-[#1A2E4A]/10">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 md:mb-16">
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] font-semibold text-[#1A2E4A] mb-6 inline-flex items-center gap-2">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#B8E024]" />
              Who we serve
            </div>
            <h2 className="font-editorial text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl">
              Thirteen industries.
              <br />
              <span className="italic">One playbook, tailored each time.</span>
            </h2>
          </div>
          <Link to="/industries" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-[#1A2E4A] hover:text-[#B8E024] transition-colors">
            All industries <ArrowUpRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {INDUSTRIES.map(({ name, img }, i) => (
            <motion.article
              key={name}
              data-testid={`industry-card-${name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.5, delay: (i % 4) * 0.06 }}
              whileHover={{ y: -6 }}
              className="group relative overflow-hidden border border-[#1A2E4A]/15 bg-white"
            >
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src={img}
                  alt={name}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A2E4A]/85 via-[#1A2E4A]/30 to-transparent" />
                <div className="absolute inset-0 p-5 flex flex-col justify-between">
                  <div className="text-[10px] font-plex-mono uppercase tracking-[0.22em] text-[#D9FF3C] flex items-center gap-2">
                    <span className="inline-block h-1 w-1 rounded-full bg-[#D9FF3C]" />
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div>
                    <div className="font-editorial text-2xl md:text-3xl leading-tight tracking-tight text-[#F1ECE0]">
                      {name}
                    </div>
                    <div className="mt-2 text-[10px] font-plex-mono uppercase tracking-[0.22em] text-[#F1ECE0]/75 flex items-center gap-1 opacity-0 translate-x-[-6px] group-hover:opacity-100 group-hover:translate-x-0 transition-all">
                      Explore <ArrowUpRight size={12} />
                    </div>
                  </div>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
